import { useState, useEffect, useRef } from "react";
import { API_URL } from "@/config/api";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Bug,
  Lightbulb,
  MessageSquare,
  X,
  Send,
  Loader2,
  Check,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";


// Global "report a problem" entry point — reachable by EVERYONE (logged-in beta
// testers AND anonymous / non-beta visitors), on every page except the
// dashboard (its bottom-right corner holds the chat composer + right rail).
// Posts to the optional-auth /api/feedback/report: attaches the user when a
// token is present, else stores anonymously with an optional reply-to email.
const KINDS = [
  { id: "bug", Icon: Bug },
  { id: "idea", Icon: Lightbulb },
  { id: "other", Icon: MessageSquare },
];

const FeedbackWidget = () => {
  const { t } = useTranslation();
  const { token, user, isAuthenticated } = useAuth();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("bug");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  // Keyboard a11y (WCAG 2.1.2 / 2.4.3): Escape closes the dialog and focus is
  // returned to whatever was focused before it opened. `closeRef` always points
  // at the latest close() so the Escape listener never goes stale, and the
  // effect only re-binds when `open` flips.
  const closeRef = useRef(() => {});
  const dialogRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const prevFocus = document.activeElement;
    const onKey = (e) => {
      if (e.key === "Escape") {
        closeRef.current();
        return;
      }
      // Focus trap (WCAG 2.4.3): keep Tab within the dialog.
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = Array.from(
          dialogRef.current.querySelectorAll(
            'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.disabled && el.offsetParent !== null);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (prevFocus && typeof prevFocus.focus === "function") prevFocus.focus();
    };
  }, [open]);

  // Keep clear of the dashboard's composer/right-rail; the in-app Settings
  // feedback + this widget on every other surface still cover logged-in users.
  if (location.pathname.startsWith("/dashboard")) return null;

  const reset = () => {
    setKind("bug");
    setMessage("");
    setEmail("");
    setSending(false);
    setSent(false);
    setError("");
  };
  const close = () => {
    setOpen(false);
    // let the closing animation-free modal reset after it's hidden
    setTimeout(reset, 150);
  };
  // Keep the Escape handler pointed at the current close().
  closeRef.current = close;

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 5 || sending) return;
    setSending(true);
    setError("");
    try {
      await axios.post(
        `${API_URL}/api/feedback/report`,
        {
          kind,
          message: message.trim(),
          email: !isAuthenticated && email.trim() ? email.trim() : undefined,
          page_path: location.pathname,
          user_agent: navigator.userAgent.slice(0, 400),
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      setSent(true);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          t("report.error", "Échec de l'envoi. Réessayez.")
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="report-open"
          className="fixed right-4 bottom-20 z-40 inline-flex items-center gap-2 rounded-full glass border border-white/10 shadow-lg px-3.5 h-11 text-sm font-medium text-slate-300 hover:text-white hover:border-primary/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
          aria-label={t("report.title", "Signaler un problème")}
        >
          <Bug className="w-4 h-4 text-primary" />
          <span className="hidden sm:inline">
            {t("report.trigger", "Signaler")}
          </span>
        </button>
      )}

      {open && (
        <div
          ref={dialogRef}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={t("report.title", "Signaler un problème")}
          onMouseDown={(e) => e.target === e.currentTarget && close()}
        >
          <div className="w-full max-w-md glass rounded-2xl border border-white/10 shadow-2xl p-6">
            <div className="flex items-start justify-between mb-1">
              <h2 className="font-heading text-lg font-bold text-white">
                {sent
                  ? t("report.successTitle", "Merci !")
                  : t("report.title", "Signaler un problème")}
              </h2>
              <button
                type="button"
                onClick={close}
                aria-label={t("report.close", "Fermer")}
                className="w-8 h-8 -mr-1 -mt-1 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {sent ? (
              <div className="py-6 text-center">
                <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-primary" />
                </div>
                <p className="text-slate-300">
                  {t(
                    "report.successMsg",
                    "Votre message est bien reçu. On regarde ça."
                  )}
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="mt-6 inline-flex items-center justify-center rounded-lg btn-glow bg-primary-strong hover:bg-primary-strong/90 text-white px-5 h-10 text-sm font-medium"
                >
                  {t("report.close", "Fermer")}
                </button>
              </div>
            ) : (
              <form onSubmit={submit}>
                <p className="text-sm text-slate-400 mb-4">
                  {t("report.subtitle", "Un bug, une idée ? Dites-nous tout.")}
                </p>

                <div className="flex gap-2 mb-4">
                  {KINDS.map(({ id, Icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setKind(id)}
                      aria-pressed={kind === id}
                      className={`flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-medium border transition-colors ${
                        kind === id
                          ? "bg-primary/10 text-primary border-primary/30"
                          : "border-white/10 text-slate-400 hover:text-white hover:border-white/20"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {t(
                        `report.kind.${id}`,
                        {
                          bug: "Bug",
                          idea: "Idée",
                          other: "Autre",
                        }[id]
                      )}
                    </button>
                  ))}
                </div>

                <textarea
                  required
                  autoFocus
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t(
                    "report.messagePlaceholder",
                    "Décrivez ce qui s'est passé…"
                  )}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-primary resize-none"
                />

                {!isAuthenticated && (
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t(
                      "report.emailPlaceholder",
                      "vous@exemple.com"
                    )}
                    aria-label={t(
                      "report.emailLabel",
                      "Email (optionnel, pour vous répondre)"
                    )}
                    className="w-full mt-3 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-primary"
                  />
                )}

                {error && (
                  <p className="mt-3 text-sm text-red-400" role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={sending || message.trim().length < 5}
                  data-testid="report-submit"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl btn-glow bg-primary-strong hover:bg-primary-strong/90 text-white h-11 text-sm font-medium disabled:opacity-60"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {sending
                    ? t("report.sending", "Envoi…")
                    : t("report.submit", "Envoyer")}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
